/**
 * DocumentVerificationStatus - Shows advertiser verification progress and document status
 * Allows document upload and resubmission for rejected documents
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Shield, ShieldCheck, ShieldAlert, CheckCircle, XCircle, Clock,
  Upload, FileText, AlertTriangle, RefreshCw, Eye, Loader2,
  ChevronRight, Info, Camera, Building, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification level colors and icons
const LEVEL_CONFIG = {
  0: { name: 'Unverified', color: 'text-gray-500', bgColor: 'bg-gray-100', icon: Shield },
  1: { name: 'Identity Verified', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: ShieldCheck },
  2: { name: 'Business Verified', color: 'text-purple-600', bgColor: 'bg-purple-100', icon: ShieldCheck },
  3: { name: 'Performance Verified', color: 'text-amber-600', bgColor: 'bg-amber-100', icon: ShieldCheck }
};

// Document type icons
const DOC_ICONS = {
  government_id: FileText,
  business_registration: Building,
  registered_address: MapPin,
  selfie: Camera,
  email: CheckCircle,
  phone: CheckCircle
};

// Status badge configuration
const STATUS_CONFIG = {
  verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Verified' },
  auto_approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Auto Approved' },
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Review' },
  processing: { color: 'bg-blue-100 text-blue-800', icon: Loader2, label: 'Processing' },
  flagged: { color: 'bg-orange-100 text-orange-800', icon: AlertTriangle, label: 'Under Review' },
  rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
  expired: { color: 'bg-gray-100 text-gray-800', icon: AlertTriangle, label: 'Expired' },
  not_submitted: { color: 'bg-gray-100 text-gray-500', icon: Upload, label: 'Not Submitted' }
};

const DocumentVerificationStatus = ({ advertiserId, advertiserType = 'private', onLevelChange }) => {
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchVerificationStatus = useCallback(async () => {
    if (!advertiserId) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/verification/documents/status/${advertiserId}`);
      setVerificationStatus(response.data);
      
      if (onLevelChange) {
        onLevelChange(response.data.verification_level);
      }
    } catch (err) {
      console.error('Error fetching verification status:', err);
    } finally {
      setLoading(false);
    }
  }, [advertiserId, onLevelChange]);

  const fetchRequirements = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification/requirements`);
      setRequirements(response.data);
    } catch (err) {
      console.error('Error fetching requirements:', err);
    }
  }, []);

  useEffect(() => {
    fetchVerificationStatus();
    fetchRequirements();
  }, [fetchVerificationStatus, fetchRequirements]);

  const handleUploadClick = (docType) => {
    setSelectedDocType(docType);
    setSelectedSubtype('');
    setShowUploadModal(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, WebP, or PDF.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('advertiser_id', advertiserId);
      formData.append('document_type', selectedDocType);
      if (selectedSubtype) {
        formData.append('document_subtype', selectedSubtype);
      }
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/verification/documents/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        toast.success('Document uploaded successfully! AI analysis in progress.');
        setShowUploadModal(false);
        // Refresh status after a short delay to allow processing
        setTimeout(() => fetchVerificationStatus(), 2000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getDocumentSubtypes = (docType) => {
    if (docType === 'government_id') {
      return [
        { value: 'nic', label: 'National ID Card (NIC)' },
        { value: 'passport', label: 'Passport' },
        { value: 'driving_license', label: "Driver's License" }
      ];
    }
    if (docType === 'registered_address') {
      return [
        { value: 'utility_bill', label: 'Utility Bill' },
        { value: 'bank_statement', label: 'Bank Statement' },
        { value: 'council_tax', label: 'Council Tax Bill' },
        { value: 'lease', label: 'Lease Agreement' }
      ];
    }
    return [];
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </CardContent>
      </Card>
    );
  }

  if (!verificationStatus) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          Unable to load verification status
        </CardContent>
      </Card>
    );
  }

  const { verification_level, documents, next_steps, progress } = verificationStatus;
  const levelConfig = LEVEL_CONFIG[verification_level] || LEVEL_CONFIG[0];
  const LevelIcon = levelConfig.icon;

  return (
    <div className="space-y-4" data-testid="document-verification-status">
      {/* Verification Level Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <LevelIcon className={`h-5 w-5 ${levelConfig.color}`} />
              Verification Status
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchVerificationStatus}
              data-testid="refresh-status-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className={`p-3 rounded-full ${levelConfig.bgColor}`}>
              <LevelIcon className={`h-6 w-6 ${levelConfig.color}`} />
            </div>
            <div>
              <p className={`font-semibold ${levelConfig.color}`}>{levelConfig.name}</p>
              <p className="text-sm text-gray-500">Level {verification_level} of 3</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Verification Progress</span>
              <span className="font-medium">{progress?.percentage || 0}%</span>
            </div>
            <Progress value={progress?.percentage || 0} className="h-2" />
            <p className="text-xs text-gray-400">
              {progress?.verified_count || 0} of {progress?.total_required || 0} documents verified
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Verification Documents</CardTitle>
          <CardDescription>
            Upload required documents to increase your verification level
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {documents?.map((doc, index) => {
            const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.not_submitted;
            const StatusIcon = statusConfig.icon;
            const DocIcon = DOC_ICONS[doc.type] || FileText;
            
            return (
              <div 
                key={doc.type}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                data-testid={`doc-item-${doc.type}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <DocIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium">{doc.display_name}</p>
                    <div className="flex items-center gap-2">
                      <Badge className={`${statusConfig.color} text-xs`}>
                        <StatusIcon className={`h-3 w-3 mr-1 ${doc.status === 'processing' ? 'animate-spin' : ''}`} />
                        {statusConfig.label}
                      </Badge>
                      {doc.ai_analysis?.confidence_score && (
                        <span className="text-xs text-gray-400">
                          AI: {Math.round(doc.ai_analysis.confidence_score)}%
                        </span>
                      )}
                    </div>
                    {doc.rejection_reason && (
                      <p className="text-xs text-red-600 mt-1">
                        Reason: {doc.rejection_reason.replace(/_/g, ' ')}
                      </p>
                    )}
                    {doc.expires_at && doc.status === 'verified' && (
                      <p className="text-xs text-gray-400 mt-1">
                        Expires: {new Date(doc.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {doc.document_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(doc.document_url, '_blank')}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  {(doc.status === 'not_submitted' || doc.can_resubmit) && (
                    <Button
                      size="sm"
                      onClick={() => handleUploadClick(doc.type)}
                      data-testid={`upload-btn-${doc.type}`}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      {doc.can_resubmit ? 'Resubmit' : 'Upload'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Add missing required documents */}
          {requirements?.document_specifications && 
            Object.keys(requirements.document_specifications)
              .filter(docType => !documents?.find(d => d.type === docType))
              .map(docType => {
                const spec = requirements.document_specifications[docType];
                const DocIcon = DOC_ICONS[docType] || FileText;
                
                return (
                  <div 
                    key={docType}
                    className="flex items-center justify-between p-3 border rounded-lg border-dashed hover:bg-gray-50 transition-colors"
                    data-testid={`doc-item-${docType}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <DocIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">{spec.display_name}</p>
                        <p className="text-xs text-gray-400">{spec.description}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUploadClick(docType)}
                      data-testid={`upload-btn-${docType}`}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                );
              })
          }
        </CardContent>
      </Card>

      {/* Next Steps */}
      {next_steps?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ChevronRight className="h-5 w-5 text-blue-500" />
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {next_steps.map((step, index) => (
                <li 
                  key={index}
                  className="flex items-start gap-2 text-sm"
                >
                  <Info className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                  <div>
                    <p>{step.description}</p>
                    <p className="text-xs text-gray-400">
                      Required for Level {step.required_for_level}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload {requirements?.document_specifications?.[selectedDocType]?.display_name || 'Document'}</DialogTitle>
            <DialogDescription>
              {requirements?.document_specifications?.[selectedDocType]?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Document subtype selector */}
            {selectedDocType && getDocumentSubtypes(selectedDocType).length > 0 && (
              <div>
                <label className="text-sm font-medium block mb-2">Document Type</label>
                <Select value={selectedSubtype} onValueChange={setSelectedSubtype}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {getDocumentSubtypes(selectedDocType).map(subtype => (
                      <SelectItem key={subtype.value} value={subtype.value}>
                        {subtype.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Accepted formats */}
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <p className="font-medium mb-1">Accepted formats:</p>
              <p className="text-gray-600">JPEG, PNG, WebP, or PDF (max 10MB)</p>
              {requirements?.document_specifications?.[selectedDocType]?.accepted_documents && (
                <div className="mt-2">
                  <p className="font-medium">Accepted documents:</p>
                  <ul className="text-gray-600 list-disc list-inside">
                    {requirements.document_specifications[selectedDocType].accepted_documents.map((doc, i) => (
                      <li key={i}>{doc}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* File input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || (getDocumentSubtypes(selectedDocType).length > 0 && !selectedSubtype)}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Select File
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentVerificationStatus;
